import React, { useEffect, useState, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import planSchema from '@galo-monitor/forms/schemas/company/plan.schema'
import I18n, { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PlanActions } from '@galo-monitor/stores/plan'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import Form from '@galo-monitor/web-components/Form'
import InputText from '@galo-monitor/web-components/InputText'

import { Container, ModulesList } from './styles'

const PlanForm = forwardRef((props, formRef) => {
  const { plan, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const [planModules, setPlanModules] = useState([])

  const { data: modules } = useGaloMonitorApi('/companies/plans/modules/list')
  const { data: planData, error: planError } = useGaloMonitorApi(plan.id ? `/companies/plans/${ plan.id }` : null)

  const handleSubmit = useCallback(
    ({ plansModules, ...data }) => {
      setLoading(true)
      dispatch(PlanActions.savePlan(
        {
          ...plan,
          ...data,
          modules_farms: planModules
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('plan', { howMany: 1 }),
            action: toLower(t(isEmpty(plan) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [setLoading, dispatch, plan, planModules, onError, snackbar, t, onSuccess]
  )

  const schemaConstructor = useCallback(
    (...params) => planSchema(...params, { plan }),
    [plan]
  )

  useEffect(() => {
    if (!isEmpty(planData))
      setPlanModules(planData.modulesFarms)
  }, [planData])

  useEffect(() => {
    if (!isEmpty(planError))
      onError(planError)
  }, [dispatch, onError, planError])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ schemaConstructor }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <InputText
              name="name"
              label={ `${ t('name of {this}', { this: t('plan', { howMany: 1 }), howMany: 1, gender: 'male' }) } Farm` }
              defaultValue={ plan.name }
              disabled={ loading }
              fullWidth
            />
          </Grid>

          <Grid item xs={ 12 }>
            <Typography variant="h6">
              {t('{this} enabled', { this: t('module', { howMany: 2 }), howMany: 2, gender: 'male' })}
              {' (Farm) '}
            </Typography>
            <ModulesList container>
              {isEmpty(modules)
                ? <Grid item xs={ 12 }><I18n>empty select options</I18n></Grid>
                : map(modules, mod => (
                  <Grid key={ mod.id } item xs={ 6 }>
                    <Checkbox
                      detached
                      id={ `${ mod.id }` }
                      label={ mod.name }
                      color="primary"
                      checked={ !!find(planModules, planModule => planModule.id === mod.id) }
                      onClick={ () => {
                        if (find(planModules, planModule => planModule.id === mod.id)) {
                          setPlanModules(prevState => filter(prevState, prevStateMod => prevStateMod.id !== mod.id))
                        } else {
                          setPlanModules(prevState => [...prevState, { id: mod.id }])
                        }
                      } }
                    />
                  </Grid>
                ))
              }
            </ModulesList>
          </Grid>
        </Grid>
      </Container>
    </Form>
  )
})

PlanForm.propTypes = {
  plan: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

PlanForm.defaultProps = {
  plan: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default PlanForm
